import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { Chat, ChatMessage } from '@/components/GenericsTournaments';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTapTourney from '@/hooks/useTapTourney';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { getTdaChatPosts, postTdaChatMessage } from '@/services/tda';
import { RequestPostPostsTDA } from '@/types/tda';

export type userTextChat = { id: string; display: string };

type Props = {
  users: userTextChat[];
  isTap?: boolean;
};

export const ChatBox: React.FC<Props> = ({ users: propUsers, isTap }) => {
  const { tapTourneyData } = useTapTourney();
  const [users, setUsers] = useState<userTextChat[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const { isDesktop } = useIsDesktop();

  const toast = useToast();
  const { tourneyData } = useTourney();

  const getTournamentId = useCallback(() => {
    return tapTourneyData?.tourneyId && isTap
      ? tapTourneyData.tourneyId
      : tourneyData.id;
  }, [tapTourneyData?.tourneyId, isTap, tourneyData.id]);

  const {
    data: chatData,
    isFetching,
    isLoading,
  } = useQuery(
    'getTdaChatPosts',
    async () => await getTdaChatPosts(getTournamentId()),
    {
      refetchOnWindowFocus: false,
      enabled: getTournamentId() !== 0 && isDesktop,
    }
  );

  useEffect(() => {
    if (chatData?.estado && !isLoading && !isFetching) {
      setMessages(
        chatData.posts.map((element) => ({
          name: element.nombre ?? '',
          msg: element.mensaje ?? '',
          time: element.fecha ?? '',
          profilePic: element?.fotoPerfil,
        }))
      );
    }
  }, [chatData, isLoading, isFetching]);

  useEffect(() => {
    if (chatData?.estado && !isLoading && !isFetching) {
      const usersFromMessages = chatData.posts.map((post) => ({
        id: post.nombre ?? '',
        display: post.nombre ?? '',
      }));

      // Combina usuarios del chat y de las props
      const combinedUsers = [...propUsers, ...usersFromMessages].filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.display === value.display)
      );

      setUsers(combinedUsers);
    }
  }, [propUsers, chatData, isLoading, isFetching]);

  const { mutateAsync: postChat } = useMutation(
    async (data: RequestPostPostsTDA) => await postTdaChatMessage(data),
    {
      onSuccess: (data) => {
        if (!data.estado) {
          toast.show(data.mensaje);
        }
      },
      onError: (err) => {
        const error = err as Error;
        toast.showError(error);
      },
    }
  );

  const handleSubmit = useCallback(
    (msg: string) => {
      // eslint-disable-next-line no-console
      console.log(`Envio de mensaje ${isTap ? 'TDAP' : 'TDA'}`);
      postChat({
        tournamentId: getTournamentId(),
        message: msg,
      });
    },
    [isTap, postChat, getTournamentId]
  );

  return (
    // CHAT GENERICO
    <Chat
      handleSubmit={handleSubmit}
      isLoading={isFetching || isLoading}
      messages={messages}
      users={users}
    />
  );
};
